import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useRole } from "../context/RoleContext";
import { getApplication, decideApplication } from "../lib/applications";
import { UploadedRow, STANDARD_CREDENTIALS, REGISTRATION_FEE_USD } from "./RegisterArchitect";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Toaster, toast } from "sonner";
import {
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  MessageSquareWarning,
  XCircle,
  ShieldCheck,
} from "lucide-react";
import BackBar from "../components/BackBar";
import WorkflowStrip from "../components/WorkflowStrip";
import StatusPill from "../components/StatusPill";

const STATE_LABEL = {
  pending_verification: "Pending verification",
  more_info_requested: "More info requested",
  approved: "Approved",
  rejected: "Rejected",
};
const STATE_STYLE = {
  pending_verification: { bg: "#1B1F4B", fg: "#FBFAFD" },
  more_info_requested: { bg: "#B98A3C", fg: "#272636" },
  approved: { bg: "#4F6149", fg: "#FBFAFD" },
  rejected: { bg: "#A23B2E", fg: "#FBFAFD" },
};

export default function AdminApplicationDetail() {
  const { applicationId } = useParams();
  const { user } = useRole();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [err, setErr] = useState(null);
  const [pendingAction, setPendingAction] = useState(null); // 'approve' | 'more_info' | 'reject' | null

  async function reload() {
    try {
      const data = await getApplication(applicationId);
      setApp(data);
    } catch (e) {
      setErr(e);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  async function doDecision(action, note) {
    try {
      const data = await decideApplication(applicationId, {
        action,
        note: note || "",
        actor_user_id: user.id,
      });
      setApp(data);
      setPendingAction(null);
      const verb =
        action === "approve"
          ? "Approved."
          : action === "reject"
          ? "Rejected."
          : "Note sent to applicant.";
      toast.success(verb);
    } catch (e) {
      const detail =
        e?.response?.data?.detail || e?.message || "Decision failed.";
      toast.error(typeof detail === "string" ? detail : "Decision failed.");
    }
  }

  if (err) {
    return (
      <section data-testid="application-detail-error">
        <p style={{ color: "#A23B2E" }}>
          Could not load this application.
        </p>
        <button
          className="wn-btn wn-btn-outline mt-4"
          onClick={() => navigate("/admin/applications")}
        >
          Back to queue
        </button>
      </section>
    );
  }

  if (!app) {
    return (
      <section data-testid="application-detail-loading">
        <div style={{ color: "#6C6B85", fontStyle: "italic" }}>
          Loading application…
        </div>
      </section>
    );
  }

  return (
    <section
      className="animate-fade-in-up"
      data-testid="admin-application-detail-page"
    >
      <Toaster position="top-right" richColors closeButton />

      <BackBar
        parent={{ to: "/admin/applications", label: "Applications" }}
        trail={[
          { label: "Admin", to: "/admin" },
          { label: "Applications", to: "/admin/applications" },
          { label: app.contact?.name },
        ]}
      />

      <div className="mt-4 flex items-center gap-3">
        <StatusPill
          value={app.state}
          testId={`state-badge-${app.state}`}
        />
        <span
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 13,
          }}
        >
          Submitted {fmtDate(app.submitted_at)}
        </span>
      </div>

      <h1
        className="mt-3"
        style={{
          fontSize: "clamp(28px, 4vw, 36px)",
          fontWeight: 600,
          lineHeight: 1.1,
        }}
      >
        {app.contact?.name}
      </h1>
      <div
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#6C6B85",
          fontSize: 16,
          marginTop: 2,
        }}
      >
        {app.firm}
      </div>

      {/* Workflow strip — Pending → Approved with halted branches for
          More-info-requested or Rejected. */}
      <WorkflowStrip
        domain="application"
        current={app.state}
        testId="application-workflow-strip"
      />

      <div className="mt-8 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-start">
        <div className="space-y-5">
          <CardBlock title="About the architect">
            {app.photo?.url && (
              <div
                className="flex items-center gap-4"
                style={{ marginBottom: 14 }}
              >
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: 999,
                    border: "1px solid #D9D8E4",
                    background: `url(${app.photo.url}) center/cover no-repeat`,
                    flexShrink: 0,
                  }}
                  aria-hidden
                  data-testid="admin-app-photo"
                />
                <div style={{ fontSize: 12, color: "#6C6B85" }}>
                  Applicant-supplied headshot. Used as the day-one profile
                  photo when approved.
                </div>
              </div>
            )}
            <KVGrid
              rows={[
                ["Name", app.contact?.name],
                ["Firm", app.firm],
                ["Firm tagline", app.firm_tagline || "—"],
                ["Email", app.contact?.email],
                ["Phone", app.contact?.phone || "—"],
                [
                  "Indiana architectural license no.",
                  <LicenseValue
                    key="license"
                    licenseNo={app.license_no}
                    verified={!!app.license_verified}
                  />,
                ],
                [
                  "Office address",
                  app.postal_address
                    ? [
                        app.postal_address.street,
                        app.postal_address.city,
                        app.postal_address.state,
                        app.postal_address.postal_code,
                      ]
                        .filter(Boolean)
                        .join(", ")
                    : "—",
                ],
                [
                  "Years practising",
                  app.years_practising == null ? "—" : app.years_practising,
                ],
                ["Year founded", app.years_founded == null ? "—" : app.years_founded],
                ["Team size", app.team_size == null ? "—" : app.team_size],
              ]}
            />
          </CardBlock>
          <CardBlock title="Practice">
            <div className="wn-label">Service areas</div>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {(app.service_areas || []).map((s) => (
                <Chip key={s}>{s}</Chip>
              ))}
            </div>
            <div className="wn-label mt-4">Typology specialisations</div>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {(app.specialisations || []).map((s) => (
                <Chip key={s}>{s}</Chip>
              ))}
            </div>
            {(app.demographic_flags || []).length > 0 && (
              <>
                <div className="wn-label mt-4">Demographic flags</div>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {app.demographic_flags.map((f) => (
                    <Chip key={f} solid>
                      {f}
                    </Chip>
                  ))}
                </div>
              </>
            )}
            <div className="wn-label mt-5">Practice statement</div>
            <p
              className="mt-1"
              style={{ color: "#272636", fontSize: 14, lineHeight: 1.6 }}
            >
              {app.practice_statement}
            </p>
            {app.bio && (
              <>
                <div className="wn-label mt-5">Biography</div>
                <p
                  className="mt-1"
                  style={{ color: "#272636", fontSize: 14, lineHeight: 1.6 }}
                  data-testid="admin-app-bio"
                >
                  {app.bio}
                </p>
              </>
            )}
            {(app.lifestyle_focuses || []).length > 0 && (
              <>
                <div className="wn-label mt-5">Lifestyle focuses</div>
                <div
                  className="flex flex-wrap gap-1.5 mt-1.5"
                  data-testid="admin-app-lifestyle"
                >
                  {app.lifestyle_focuses.map((f) => (
                    <Chip key={f}>{f}</Chip>
                  ))}
                </div>
              </>
            )}
            {(app.credentials || []).filter((c) => c.abbr || c.name).length > 0 && (
              <>
                <div className="wn-label mt-5">Credentials</div>
                <ul
                  className="mt-1.5"
                  style={{
                    color: "#272636",
                    fontSize: 13.5,
                    lineHeight: 1.6,
                    listStyle: "none",
                    paddingLeft: 0,
                  }}
                  data-testid="admin-app-credentials"
                >
                  {app.credentials
                    .filter((c) => c.abbr || c.name)
                    .map((c, i) => (
                      <li key={i}>
                        <span
                          className="tabular-nums"
                          style={{ fontWeight: 700, marginRight: 8 }}
                        >
                          {c.abbr || "—"}
                        </span>
                        {c.name || ""}
                        {c.year ? (
                          <span
                            className="tabular-nums"
                            style={{ color: "#6C6B85", marginLeft: 8 }}
                          >
                            {c.year}
                          </span>
                        ) : null}
                      </li>
                    ))}
                </ul>
              </>
            )}
            {(app.standard_credentials || []).length > 0 && (
              <>
                <div className="wn-label mt-5">Green-building credentials</div>
                <div
                  className="flex flex-wrap gap-1.5 mt-1.5"
                  data-testid="admin-app-standard-credentials"
                >
                  {app.standard_credentials.map((k) => {
                    const item = STANDARD_CREDENTIALS.find((s) => s.key === k);
                    return (
                      <span
                        key={k}
                        style={{
                          padding: "3px 10px",
                          borderRadius: 999,
                          backgroundColor: "rgba(177,78,47,0.10)",
                          color: "#8F3C23",
                          fontSize: 11,
                          fontWeight: 700,
                          letterSpacing: "0.04em",
                        }}
                      >
                        {item ? item.label : k}
                      </span>
                    );
                  })}
                </div>
              </>
            )}
          </CardBlock>

          <CardBlock title="Supporting documents">
            <div className="space-y-2" data-testid="application-docs-admin">
              {app.documents?.license && (
                <UploadedRow
                  file={app.documents.license}
                  testidPrefix="admin-doc-license"
                />
              )}
              {app.documents?.eo && (
                <UploadedRow
                  file={app.documents.eo}
                  testidPrefix="admin-doc-eo"
                />
              )}
              {app.documents?.professional_liability && (
                <UploadedRow
                  file={app.documents.professional_liability}
                  testidPrefix="admin-doc-prof-liability"
                />
              )}
              {(app.documents?.portfolio || []).map((p, i) => (
                <UploadedRow
                  key={p.id || i}
                  file={p}
                  testidPrefix={`admin-doc-portfolio-${i}`}
                />
              ))}
              {!app.documents?.license &&
                !app.documents?.eo &&
                !app.documents?.professional_liability &&
                !(app.documents?.portfolio || []).length && (
                  <div style={{ color: "#6C6B85", fontSize: 13 }}>
                    No documents on file.
                  </div>
                )}
            </div>
            <div className="mt-3" style={{ fontSize: 11, color: "#6C6B85" }}>
              Click the file name on a document row to view it in a new tab.
            </div>
            <div className="mt-2 flex flex-wrap gap-2" data-testid="doc-quick-links">
              {[
                app.documents?.license,
                app.documents?.eo,
                app.documents?.professional_liability,
                ...(app.documents?.portfolio || []),
              ]
                .filter(Boolean)
                .map((d, i) => (
                  <a
                    key={d.id || i}
                    href={d.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wn-btn wn-btn-tertiary"
                    style={{ height: 30, fontSize: 12 }}
                    data-testid={`view-document-${i}`}
                  >
                    View {d.label || "document"}
                  </a>
                ))}
            </div>
          </CardBlock>

          <RegistrationFeeBlock app={app} />

          {(app.state_history || []).length > 1 && (
            <CardBlock title="Submission history">
              <ol className="space-y-3" data-testid="state-history">
                {app.state_history.map((h, i) => (
                  <li
                    key={i}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "120px 1fr",
                      gap: 14,
                    }}
                  >
                    <div
                      className="tabular-nums"
                      style={{ fontSize: 12, color: "#3A5563", fontWeight: 700 }}
                    >
                      {fmtDate(h.at)}
                    </div>
                    <div>
                      <div
                        style={{ fontSize: 13, fontWeight: 700, color: "#1B1F4B" }}
                      >
                        {STATE_LABEL[h.state] || h.state}
                      </div>
                      {h.note && (
                        <div
                          style={{
                            color: "#6C6B85",
                            fontSize: 12,
                            lineHeight: 1.6,
                            marginTop: 2,
                          }}
                        >
                          {h.note}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </CardBlock>
          )}
        </div>

        <DecisionPanel
          app={app}
          onApprove={() => setPendingAction("approve")}
          onMoreInfo={() => setPendingAction("more_info")}
          onReject={() => setPendingAction("reject")}
        />
      </div>

      <ApproveDialog
        open={pendingAction === "approve"}
        onClose={() => setPendingAction(null)}
        onConfirm={(note) => doDecision("approve", note)}
        firm={app.firm}
      />
      <MoreInfoDialog
        open={pendingAction === "more_info"}
        onClose={() => setPendingAction(null)}
        onConfirm={(note) => doDecision("request_more_info", note)}
      />
      <RejectDialog
        open={pendingAction === "reject"}
        onClose={() => setPendingAction(null)}
        onConfirm={(note) => doDecision("reject", note)}
      />

      <div className="mt-10">
        <button
          className="wn-btn wn-btn-tertiary"
          onClick={() => navigate("/admin/applications")}
          data-testid="back-to-queue-btn"
        >
          <ChevronLeft size={16} /> Back to queue
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
function DecisionPanel({ app, onApprove, onMoreInfo, onReject }) {
  if (app.state === "pending_verification") {
    return (
      <div
        className="wn-card"
        style={{ padding: "22px 22px" }}
        data-testid="decision-panel"
      >
        <h3
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 600,
            fontSize: 19,
            color: "#1B1F4B",
          }}
        >
          Decision
        </h3>
        <p
          className="mt-1"
          style={{ color: "#6C6B85", fontSize: 13, lineHeight: 1.55 }}
        >
          Choose how this application should proceed. Approvals trigger
          immediate account activation and profile publication.
        </p>
        <div className="mt-5 grid gap-3">
          <button
            className="wn-btn wn-btn-accent"
            onClick={onApprove}
            data-testid="approve-btn"
          >
            <CheckCircle2 size={16} /> Approve
          </button>
          <button
            className="wn-btn wn-btn-outline"
            onClick={onMoreInfo}
            data-testid="request-more-info-btn"
          >
            <MessageSquareWarning size={16} /> Request more information
          </button>
          <button
            className="wn-btn wn-btn-tertiary"
            onClick={onReject}
            style={{ color: "#A23B2E" }}
            data-testid="reject-btn"
          >
            <XCircle size={16} /> Reject
          </button>
        </div>
      </div>
    );
  }

  if (app.state === "more_info_requested") {
    return (
      <div
        className="wn-card"
        style={{
          padding: "22px 22px",
          borderLeft: "3px solid #B98A3C",
        }}
        data-testid="decision-panel"
      >
        <h3
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 600,
            fontSize: 19,
            color: "#1B1F4B",
          }}
        >
          Awaiting the architect&apos;s response.
        </h3>
        <div className="wn-label mt-4" style={{ color: "#8a651f" }}>
          Note you sent
        </div>
        <p
          className="mt-1"
          style={{ color: "#272636", fontSize: 14, lineHeight: 1.55 }}
        >
          {app.review_notes || "—"}
        </p>
        <p
          className="mt-4"
          style={{ color: "#6C6B85", fontSize: 12, lineHeight: 1.55 }}
        >
          The applicant will see this on their status page and can update and
          resubmit at any time.
        </p>
      </div>
    );
  }

  // approved / rejected
  return (
    <div
      className="wn-card"
      style={{
        padding: "22px 22px",
        borderLeft: `3px solid ${
          app.state === "approved" ? "#4F6149" : "#A23B2E"
        }`,
      }}
      data-testid="decision-panel"
    >
      <h3
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 600,
          fontSize: 19,
          color: "#1B1F4B",
        }}
      >
        {app.state === "approved" ? "Approved." : "Rejected."}
      </h3>
      <p
        className="mt-2 tabular-nums"
        style={{ fontSize: 12, color: "#3A5563", fontWeight: 700 }}
      >
        {app.reviewed_at ? fmtDate(app.reviewed_at) : ""}
      </p>
      {app.review_notes && (
        <>
          <div className="wn-label mt-4">Review notes</div>
          <p
            className="mt-1"
            style={{ color: "#272636", fontSize: 14, lineHeight: 1.55 }}
          >
            {app.review_notes}
          </p>
        </>
      )}
      <p
        className="mt-4"
        style={{
          color: "#6C6B85",
          fontSize: 12,
          fontStyle: "italic",
          fontFamily: '"Newsreader", Georgia, serif',
        }}
      >
        This is the final state of this application.
      </p>
      {app.state === "approved" && app.architect_id && (
        <Link
          to={`/architects/${app.architect_id}`}
          className="wn-btn wn-btn-outline mt-4"
          data-testid="view-architect-profile-btn"
        >
          View public profile <ChevronRight size={14} />
        </Link>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function ApproveDialog({ open, onClose, onConfirm, firm }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  function reset() {
    setNote("");
    setBusy(false);
  }

  async function go() {
    setBusy(true);
    await onConfirm(note);
    reset();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent
        className="border"
        style={{
          backgroundColor: "#FBFAFD",
          borderColor: "#D9D8E4",
          borderRadius: 12,
          fontFamily: '"Nunito Sans", sans-serif',
          maxWidth: 520,
        }}
        data-testid="approve-dialog"
      >
        <DialogHeader>
          <DialogTitle
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 22,
              color: "#1B1F4B",
            }}
          >
            Approve this architect?
          </DialogTitle>
          <DialogDescription
            style={{ color: "#6C6B85", fontSize: 13, lineHeight: 1.6 }}
          >
            On approval, {firm || "this firm"} will be added to the verified
            network and the following will happen immediately:
          </DialogDescription>
        </DialogHeader>
        <ul
          style={{
            fontSize: 13,
            color: "#272636",
            lineHeight: 1.75,
            paddingLeft: 18,
            margin: "8px 0 0",
          }}
        >
          <li>The architect&apos;s account is activated.</li>
          <li>Their public profile is published immediately.</li>
          <li>
            The <strong>Verified Network Architect</strong> badge is applied
            across the platform.
          </li>
          <li>Notifications are sent to the architect.</li>
        </ul>
        <div className="mt-4">
          <label className="wn-label">Optional internal notes</label>
          <textarea
            className="wn-input mt-1"
            style={{ minHeight: 90, padding: "10px" }}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional internal notes on this approval."
            data-testid="approve-note"
          />
        </div>
        <DialogFooter className="mt-3">
          <button
            className="wn-btn wn-btn-tertiary"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={busy}
            data-testid="approve-cancel-btn"
          >
            Cancel
          </button>
          <button
            className="wn-btn wn-btn-primary"
            onClick={go}
            disabled={busy}
            data-testid="approve-confirm-btn"
          >
            {busy ? "Approving…" : "Confirm approval"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MoreInfoDialog({ open, onClose, onConfirm }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const tooShort = note.trim().length < 20;

  function reset() {
    setNote("");
    setBusy(false);
  }

  async function go() {
    if (tooShort) return;
    setBusy(true);
    await onConfirm(note);
    reset();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent
        className="border"
        style={{
          backgroundColor: "#FBFAFD",
          borderColor: "#D9D8E4",
          borderRadius: 12,
          fontFamily: '"Nunito Sans", sans-serif',
          maxWidth: 560,
        }}
        data-testid="more-info-dialog"
      >
        <DialogHeader>
          <DialogTitle
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 22,
              color: "#1B1F4B",
            }}
          >
            Request more information.
          </DialogTitle>
          <DialogDescription
            style={{ color: "#6C6B85", fontSize: 13, lineHeight: 1.6 }}
          >
            The architect will be notified and will be able to update their
            application and resubmit.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-3">
          <label className="wn-label">
            Note to the architect (required, ≥ 20 characters)
          </label>
          <textarea
            className="wn-input mt-1"
            style={{ minHeight: 130, padding: "10px" }}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Describe what the architect needs to update or provide. This message will be shown to them on their application status page."
            data-testid="more-info-note"
          />
          <div
            className="mt-1 tabular-nums"
            style={{ fontSize: 11, color: "#6C6B85" }}
          >
            {note.trim().length} / 20
          </div>
        </div>
        <DialogFooter className="mt-3">
          <button
            className="wn-btn wn-btn-tertiary"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={busy}
            data-testid="more-info-cancel-btn"
          >
            Cancel
          </button>
          <button
            className="wn-btn wn-btn-primary"
            onClick={go}
            disabled={busy || tooShort}
            data-testid="more-info-confirm-btn"
          >
            {busy ? "Sending…" : "Send request"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ open, onClose, onConfirm }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const tooShort = note.trim().length < 20;

  function reset() {
    setNote("");
    setBusy(false);
  }

  async function go() {
    if (tooShort) return;
    setBusy(true);
    await onConfirm(note);
    reset();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent
        className="border"
        style={{
          backgroundColor: "#FBFAFD",
          borderColor: "#D9D8E4",
          borderRadius: 12,
          fontFamily: '"Nunito Sans", sans-serif',
          maxWidth: 540,
        }}
        data-testid="reject-dialog"
      >
        <DialogHeader>
          <DialogTitle
            style={{
              fontFamily: '"Fraunces", Georgia, serif',
              fontWeight: 600,
              fontSize: 22,
              color: "#1B1F4B",
            }}
          >
            Reject this application?
          </DialogTitle>
          <DialogDescription
            style={{ color: "#6C6B85", fontSize: 13, lineHeight: 1.6 }}
          >
            This application will be marked rejected and the architect will be
            notified. They may submit a new application in the future if their
            circumstances change.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-3">
          <label className="wn-label">
            Reason for rejection (required, ≥ 20 characters)
          </label>
          <textarea
            className="wn-input mt-1"
            style={{ minHeight: 130, padding: "10px" }}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Explain the reason for rejection. This will be shown to the architect."
            data-testid="reject-note"
          />
          <div
            className="mt-1 tabular-nums"
            style={{ fontSize: 11, color: "#6C6B85" }}
          >
            {note.trim().length} / 20
          </div>
        </div>
        <DialogFooter className="mt-3">
          <button
            className="wn-btn wn-btn-tertiary"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={busy}
            data-testid="reject-cancel-btn"
          >
            Cancel
          </button>
          <button
            className="wn-btn wn-btn-outline"
            onClick={go}
            disabled={busy || tooShort}
            style={{ color: "#A23B2E", borderColor: "#1B1F4B" }}
            data-testid="reject-confirm-btn"
          >
            {busy ? "Rejecting…" : "Confirm rejection"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
function CardBlock({ title, children }) {
  return (
    <div
      className="wn-card"
      style={{ padding: "20px 22px" }}
      data-testid={`card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <h3
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontWeight: 600,
          fontSize: 18,
          color: "#1B1F4B",
        }}
      >
        {title}
      </h3>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function KVGrid({ rows }) {
  return (
    <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt className="wn-label">{k}</dt>
          <dd
            style={{
              marginTop: 2,
              color: "#272636",
              fontSize: 14,
              fontWeight: 600,
            }}
          >
            {v || "—"}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function Chip({ children, solid }) {
  return (
    <span
      style={{
        padding: "3px 10px",
        borderRadius: 999,
        backgroundColor: solid ? "#5B6196" : "rgba(91,97,150,0.10)",
        color: solid ? "#FBFAFD" : "#1B1F4B",
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.04em",
      }}
    >
      {children}
    </span>
  );
}

function LicenseValue({ licenseNo, verified }) {
  return (
    <span
      className="inline-flex items-center"
      style={{ gap: 8, flexWrap: "wrap" }}
      data-testid="admin-app-license-value"
    >
      <span style={{ fontWeight: 600 }}>{licenseNo || "—"}</span>
      {verified ? (
        <span
          className="inline-flex items-center"
          style={{
            gap: 4,
            padding: "2px 8px",
            borderRadius: 999,
            background: "rgba(79,97,73,0.10)",
            color: "#4F6149",
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            border: "1px solid rgba(79,97,73,0.32)",
          }}
          data-testid="admin-app-ipla-verified"
        >
          <ShieldCheck size={11} /> IPLA verified
        </span>
      ) : (
        <span
          style={{
            padding: "2px 8px",
            borderRadius: 999,
            background: "rgba(162,59,46,0.08)",
            color: "#A23B2E",
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            border: "1px solid rgba(162,59,46,0.28)",
          }}
          data-testid="admin-app-ipla-unverified"
        >
          Unverified
        </span>
      )}
    </span>
  );
}

function RegistrationFeeBlock({ app }) {
  const amount =
    app.registration_fee_amount_usd == null
      ? REGISTRATION_FEE_USD
      : app.registration_fee_amount_usd;
  const status = app.registration_fee_status || "pending";
  const label = status === "paid" ? "Paid" : "Pending — invoiced on approval";
  return (
    <div
      className="wn-card"
      style={{
        padding: "16px 20px",
        display: "grid",
        gridTemplateColumns: "1fr auto",
        alignItems: "center",
        gap: 16,
      }}
      data-testid="admin-app-registration-fee"
    >
      <div>
        <div className="wn-label" style={{ color: "#8F3C23" }}>
          Registration fee
        </div>
        <div
          style={{
            marginTop: 4,
            fontSize: 13,
            color: "#272636",
            lineHeight: 1.55,
          }}
        >
          {label}
        </div>
      </div>
      <div
        className="tabular-nums"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: 24,
          fontWeight: 600,
          color: "#1B1F4B",
        }}
      >
        ${amount}
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}
